import axios from 'axios';

const API_URL = 'https://admin.krazybets.online';

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Bets API
export const betsApi = {
  getCategories: () => api.get('/bets/categories'),
  browse: (params) => api.get('/bets/browse', { params }),
  getTrending: (limit = 10) => api.get('/bets/trending', { params: { limit } }),
  getById: (id) => api.get(`/bets/${id}`),
  create: (data) => api.post('/bets/create', data),
  match: (data) => api.post('/bets/match', data),
  getMyBets: () => api.get('/bets/my-bets')
};

// Wallet API
export const walletApi = {
  getBalance: () => api.get('/wallet/balance'),
  getTransactions: (limit = 50) => api.get('/wallet/transactions', { params: { limit } }),
  transferQbean: (data) => api.post('/wallet/transfer-qbean', data)
};

// Payments API
export const paymentsApi = {
  createDeposit: (data) => api.post('/payments/deposit', data),
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`)
};

// RPS API
export const rpsApi = {
  getChallenges: (limit = 20) => api.get('/rps/challenges', { params: { limit } }),
  getMyChallenges: () => api.get('/rps/my-challenges'),
  getLeaderboard: (limit = 10) => api.get('/rps/leaderboard', { params: { limit } }),
  create: (data) => api.post('/rps/create', data),
  accept: (challengeId) => api.post(`/rps/accept/${challengeId}`),
  commitMove: (data) => api.post('/rps/move/commit', data),
  revealMove: (data) => api.post('/rps/move/reveal', data)
};

// Admin API
export const adminApi = {
  getPendingBets: () => api.get('/admin/pending-bets'),
  approveBet: (betId) => api.post(`/admin/approve-bet/${betId}`),
  rejectBet: (betId, reason) => api.post(`/admin/reject-bet/${betId}`, null, { params: { reason } }),
  resolveBet: (betId, winningOption) => api.post(`/admin/resolve-bet/${betId}`, null, { params: { winning_option: winningOption } }),
  getStats: () => api.get('/admin/stats'),
  getDashboardStats: () => api.get('/admin/dashboard-stats'),
  getUsers: (limit = 50, offset = 0) => api.get('/admin/users', { params: { limit, offset } }),
  updateUserRole: (userId, role) => api.post(`/admin/update-user-role/${userId}`, null, { params: { role } }),
  getAllBets: (params) => api.get('/admin/all-bets', { params }),
  cancelBet: (betId) => api.post(`/admin/cancel-bet/${betId}`),
  pauseBet: (betId) => api.post(`/admin/pause-bet/${betId}`),
  topupUser: (userId, amount, currency) => api.post(`/admin/topup-user/${userId}`, null, { params: { amount, currency } }),
  deleteUser: (userId) => api.delete(`/admin/delete-user/${userId}`),
  getKYCRequests: () => api.get('/admin/kyc-requests'),
  approveKYC: (userId) => api.post(`/admin/approve-kyc/${userId}`),
  getTransactions: (limit, offset) => api.get('/admin/transactions', { params: { limit, offset } }),
  getDisputes: () => api.get('/admin/disputes'),
  getRPSGames: (limit) => api.get('/admin/rps-games', { params: { limit } }),
  getSupportTickets: () => api.get('/admin/support-tickets'),
  generateAIBets: (count) => api.post(`/admin/ai-generate-bets`, null, { params: { count } }),
  creditNPCs: (amount) => api.post(`/admin/credit-npcs`, null, { params: { amount } }),

  // Super Admin v2
  overviewV2: () => api.get('/admin/overview-v2'),
  growthCharts: (days = 30) => api.get('/admin/growth-charts', { params: { days } }),
  usersEnriched: (params) => api.get('/admin/users-enriched', { params }),
  userProfile: (userId) => api.get(`/admin/user-profile/${userId}`),
  userAction: (userId, action, reason = '-') =>
    api.post(`/admin/user-action/${userId}`, null, { params: { action, reason } }),
  kycPanel: (status) => api.get('/admin/kyc-panel', { params: status ? { status } : {} }),
  kycApprove: (userId, comment = '') => api.post(`/admin/kyc-approve/${userId}`, null, { params: { comment } }),
  kycReject: (userId, comment) => api.post(`/admin/kyc-reject/${userId}`, null, { params: { comment } }),
  betsEnriched: (params) => api.get('/admin/bets-enriched', { params }),
  betAction: (betId, action, comment = '') =>
    api.post(`/admin/bet-action/${betId}`, null, { params: { action, comment } }),
  transactionsEnriched: (params) => api.get('/admin/transactions-enriched', { params }),
  rpsEnriched: (params) => api.get('/admin/rps-enriched', { params }),
  aiInsights: () => api.get('/admin/ai-insights'),
  auditLog: (limit = 200) => api.get('/admin/audit-log', { params: { limit } }),
  geoStats: () => api.get('/admin/geo-stats'),
  leaderboard: (metric = 'wins', limit = 20) => api.get('/admin/leaderboard', { params: { metric, limit } }),
  referralStats: () => api.get('/admin/referral-stats'),
  exportTable: (table) => api.get(`/admin/export/${table}`, { responseType: 'blob' }),
  contactMessages: (limit = 100) => api.get('/admin/contact-messages', { params: { limit } }),
  contactMessageStatus: (id, status) =>
    api.post(`/admin/contact-message-status/${id}`, null, { params: { status } }),
};

// Contact API (public)
export const contactApi = {
  submit: (data) => api.post('/contact/submit', data),
};

// Notifications API
export const notificationsApi = {
  getAll: (limit = 20) => api.get('/notifications/', { params: { limit } }),
  markRead: (id) => api.post(`/notifications/read/${id}`),
  markAllRead: () => api.post('/notifications/read-all')
};

// Tickets API
export const ticketsApi = {
  create: (data) => api.post('/tickets/create', data),
  getMy: () => api.get('/tickets/my')
};

// Players API
export const playersApi = {
  getOnline: () => api.get('/players/online')
};

// Referrals API
export const referralsApi = {
  me: () => api.get('/referrals/me'),
  leaderboard: (limit = 10) => api.get('/referrals/leaderboard', { params: { limit } }),
};

// Public API
export const publicApi = {
  getStats: () => api.get('/stats'),
  getSubscriptionTiers: () => api.get('/subscription-tiers')
};

export default api;
